import React, { useState, useMemo } from "react";
import {
  Space,
  Table,
  Tag,
  Typography,
  message,
  Popconfirm,
  Tooltip,
  theme,
} from "antd";
import { DeleteOutlined, ExportOutlined, InfoCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import InvestigationModal from "./InvestigationModal";
import InvestigationsUploadModal from "./InvestigationsUploadModal";
import { statusColors } from "../../../helpers/medicineKeys";
import { deletePatientatientInvestigationsAPI } from "../../../services/patient.services";
import InvestigationGallery from "./InvestigationGallery/InvestigationGallery";
import dayjs from "dayjs";
import useQuery from "../../../hooks/useQuery";
import useIsMobile from "../../../hooks/useIsMobile";
import useTabletLayout from "../../../hooks/useTabletLayout";
import "./InvestigationsIp.css";

const { Paragraph } = Typography;

const getMobileColWidth = (title) => {
  const label = typeof title === "string" ? title : "";
  return Math.max(88, label.length * 7 + 28);
};

const InvestigationTable = ({
  data,
  refreshPage,
  id,
  patientData,
  nameSearch = "",
}) => {
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const isTabletLayout = useTabletLayout();
  const [editData, setEditData] = useState(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [galleryModal, setGalleryModal] = useState(false);
  const isActive = useQuery()?.status === "current";

  const filteredData = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();
    if (!query) return data || [];
    return (data || []).filter(
      (record) =>
        record?.name?.toString().toLowerCase().includes(query) ||
        record?.investigation_name?.toString().toLowerCase().includes(query)
    );
  }, [data, nameSearch]);

  const deleteInvestigation = (investigationId) => {
    deletePatientatientInvestigationsAPI(investigationId)
      .then((res) => {
        message.success(res?.data?.message);
        refreshPage();
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const renderStatus = (value, record) => (
    <>
      <Tag color={statusColors?.[value]}>{value?.replace("_", " ")}</Tag>
      <Tooltip
        title={
          <>
            <Typography style={{ color: "#fff" }}>
              Ordered By : {record?.ordering_doctor_name}
            </Typography>
            <Typography style={{ color: "#fff" }}>
              Ordered Date :{" "}
              {dayjs(record?.created_at)?.format("DD,MMM YYYY hh:mm A")}
            </Typography>
          </>
        }
      >
        <InfoCircleOutlined style={{ color: "blue" }} />
      </Tooltip>
    </>
  );

  const renderActions = (record) => (
    <Space size={isMobile ? "small" : "middle"}>
      {record?.status === "Report_Delivered" && (
        <ActionIconButton
          aria-label="View report"
          onClick={() => {
            setGalleryModal(true);
            setEditData(record);
          }}
        >
          <ExportOutlined style={{ color: token.colorPrimary, fontSize: 16 }} />
        </ActionIconButton>
      )}
      {record?.is_initial ? (
        <ActionIconButton
          aria-label="Upload investigation"
          onClick={() => {
            setUploadModal(true);
            setEditData(record);
          }}
        >
          <EditIcon />
        </ActionIconButton>
      ) : (
        <ActionIconButton
          aria-label="Edit investigation"
          onClick={() => {
            setOpen(true);
            setEditData(record);
          }}
        >
          <EditIcon />
        </ActionIconButton>
      )}
      {record?.status !== "Report_Delivered" && (
        <Popconfirm
          title="Sure to delete"
          onConfirm={() => deleteInvestigation(record?.id)}
        >
          <ActionIconButton aria-label="Delete investigation" danger>
            <DeleteOutlined style={{ color: "red", fontSize: 16 }} />
          </ActionIconButton>
        </Popconfirm>
      )}
    </Space>
  );

  const columns = [
    {
      title: "Investigation",
      render: (record) => record?.name || record?.investigation_name,
      width: isMobile ? 180 : "20%",
    },
    {
      title: "Report Delivered On",
      render: (value, record) =>
        record?.investigation_date || record?.delivery_on
          ? dayjs(record?.investigation_date || record?.delivery_on)?.format(
              "MMM DD"
            )
          : "-",
      width: isMobile ? getMobileColWidth("Report Delivered On") : "15%",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: isMobile ? 130 : "20%",
      render: (value, record) => renderStatus(value, record),
    },
    {
      title: "Notes",
      dataIndex: "comments",
      key: "comments",
      width: isMobile ? 140 : isTabletLayout ? 140 : 180,
      ellipsis: true,
      render: (value) =>
        value ? (
          <Paragraph
            className="investigations-notes-cell"
            ellipsis={{
              rows: 2,
              expandable: true,
              symbol: "more",
            }}
            style={{
              width: "100%",
              maxWidth: "100%",
              marginBottom: 0,
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {value}
          </Paragraph>
        ) : (
          "-"
        ),
    },
  ];

  if (isActive) {
    columns.push({
      title: "Action",
      width: isMobile ? 100 : isTabletLayout ? 110 : 100,
      fixed: isMobile || isTabletLayout ? "right" : undefined,
      render: (record) => renderActions(record),
    });
  }

  const mobileScrollX = isMobile
    ? Math.max(
        columns.reduce(
          (sum, col) =>
            sum + (typeof col.width === "number" ? col.width : 88),
          0
        ),
        640
      )
    : isTabletLayout
      ? Math.max(
          columns.reduce(
            (sum, col) =>
              sum + (typeof col.width === "number" ? col.width : 100),
            0
          ),
          720
        )
      : undefined;

  return (
    <>
      <div className="investigations-table-scroll">
        <Table
          className="no-vertical-borders investigations-data-table"
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          bordered
          tableLayout={isMobile ? "fixed" : undefined}
          scroll={
            isMobile || isTabletLayout
              ? { x: mobileScrollX }
              : { y: "calc(100vh - 200px)", x: 0 }
          }
        />
      </div>
      {open && (
        <InvestigationModal
          refreshPage={refreshPage}
          open={open}
          setOpen={setOpen}
          editData={editData}
        />
      )}
      {uploadModal && (
        <InvestigationsUploadModal
          open={uploadModal}
          setOpen={setUploadModal}
          editData={editData}
          refreshPage={refreshPage}
        />
      )}
      {galleryModal && (
        <InvestigationGallery
          open={galleryModal}
          setOpen={setGalleryModal}
          editData={editData}
          id={id}
          patientData={patientData}
        />
      )}
    </>
  );
};

export default InvestigationTable;
